import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TestCentersService } from '../../../services/test-centers.service';
import { SessionInfoStorageService } from '../../../services/session-info-storage.service';
import { ProfileService } from '../../../services/profile.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalService } from 'src/app/services/modal.service';
import { Meta, Title } from "@angular/platform-browser";
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-test-centers',
  templateUrl: './test-centers.component.html',
  styleUrls: ['./test-centers.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TestCentersComponent implements OnInit, OnDestroy {
  title = 'Local Lab Test Centers | Find a Grassroots Labs Location Near You';
  defaultTitle = 'Grassroots Labs - Blood Tests are Expensive. We fix that.';
  metaDescription = 'Locate your nearest Grassroots Labs test center and enjoy hassle-free access to affordable lab testing. Our nationwide locations are ready to serve you with transparent pricing and reliable service.';

  isEmpty = false;
  viewType = 'roadmap';
  isCollapsed = false;
  isValidFormSubmitted = false;

  nearestCenters: any[] = [];
  centers = { items: [], count: 0 };

  testCentersForm: UntypedFormGroup;
  private subscriptions: Subscription[] = [];

  get zipCode() {
    return this.testCentersForm.get('zipCode');
  }

  constructor(
    private testCentersService: TestCentersService,
    private sessionInfoStorage: SessionInfoStorageService,
    private modalService: ModalService,
    private metaService: Meta,
    private titleService: Title,
    private profileService: ProfileService,
    private fb: UntypedFormBuilder
  ) {
    this.testCentersForm = this.fb.group({
      zipCode: [null, [Validators.required, Validators.pattern(/^\d{5}$/)]]
    });
  }

  ngOnInit(): void {
    this.initMap();
    this.titleService.setTitle(this.title);
    this.metaService.updateTag({ name: 'description', content: this.metaDescription });

    if (this.sessionInfoStorage.getToken()) {
      const profileSub = this.profileService.getProfile(this.sessionInfoStorage.getProfileId())
        .subscribe((profile: any) => {
          const zip = profile?.homeAddress?.zip || profile?.billingAddress?.zip || profile?.govAddress?.zip;
          if (zip) this.testCentersForm.patchValue({ zipCode: zip });
        });

      this.subscriptions.push(profileSub);
    }
  }

  ngOnDestroy(): void {
    this.titleService.setTitle(this.defaultTitle);
    this.metaService.removeTag('name="description"');
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  search(): void {
    if (this.testCentersForm.invalid) return;

    const zipCode = this.zipCode?.value;
    this.testCentersService.findTestCenters(zipCode).then((result: any) => {
      this.isValidFormSubmitted = true;
      this.nearestCenters = result.map(center => ({
        ...center,
        workingHours: this.formatWorkingHours(center.workingHours)
      }));

      this.isEmpty = !this.nearestCenters.length;
      if (this.isEmpty) this.modalService.showTopNotification(true, 'Nothing found. Try another Zip.');
    });
  }

  private formatWorkingHours(workingHours: string): string {
    return workingHours.replace(/; |\|/g, '\n')
      .replace(/ ([ap])m/g, '$1m')
      .replace(/([ap])m-/g, '$1m-')
      .replace(/m-/g, 'm ~ ')
      .split('-')
      .map(part => part.replace(/([a-zA-Z]) (\d)/g, '$1: $2'))
      .join('-')
      .replace(/m ~ /g, 'm - ');
  }

  trackByFn(index: number, _item: any): number {
    return index;
  }

  async initMap(): Promise<void> {
    if (!window['google'] || !window['google'].maps) await this.loadGoogleMaps();

    const map = new google.maps.Map(document.getElementById('map') as HTMLElement, {
      center: { lat: -34.397, lng: 150.644 },
      zoom: 8,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    });

    this.nearestCenters.forEach(center => {
      new google.maps.Marker({
        position: { lat: center.latitude, lng: center.longitude },
        map,
        title: center.name
      });
    });
  }

  private loadGoogleMaps(): Promise<void> {
    return new Promise(resolve => {
      if (document.getElementById('google-maps-script')) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBQXddZ6e3vFwNZxbmTVXhu-ZV_kPYHuO0&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }
}
